/**
 * Per-cell element pip strip. Renders one row of ElementIcon SVGs
 * anchored at the LOCAL VIEWER's bottom-near edge of every grid cell
 * that contains at least one Site. Position + scale are mutated each
 * frame by GameBoard.updateCellElementPipOverlays via the registerRefs
 * hook. React only re-renders when the `cells` array itself churns.
 *
 * v1.69. Orientation is the VIEWER's, not the site owner's. Each
 * player sees one strip per cell at their bottom edge, oriented
 * upright for them. Mixed-ownership sites in one cell aggregate into
 * the single per-cell pip count.
 */
import ElementIcon from '../deckStats/ElementIcon';

// Render order. Matches the deck-stats convention so the strip is
// recognisable across surfaces. Air / Earth / Fire / Water.
const ORDER = ['Air', 'Earth', 'Fire', 'Water'];

export default function CellElementPips({ cells, registerRefs }) {
  if (!cells || cells.length === 0) return null;
  return (
    <div aria-hidden="true">
      {cells.map((cell) => (
        <Strip
          key={`${cell.cellKey}|${cell.viewerRotated ? 'r' : 'n'}`}
          cell={cell}
          registerRefs={registerRefs}
        />
      ))}
    </div>
  );
}

function Strip({ cell, registerRefs }) {
  const setRef = (el) => registerRefs(`${cell.cellKey}|${cell.viewerRotated ? 'r' : 'n'}`, el);
  // Total pip count drives the strip width; each pip is a fixed size.
  const pips = [];
  for (const element of ORDER) {
    const key = element.toLowerCase();
    const count = cell.elements?.[key] || 0;
    for (let i = 0; i < count; i++) pips.push({ element, i });
  }
  if (pips.length === 0) return null;
  // No inline transform. The projector composes translate + 180°
  // viewer rotation as one transform string per frame so we don't
  // double-rotate. The base CSS class handles flex layout + spacing.
  return (
    <div
      ref={setRef}
      className="cell-element-pip-strip"
    >
      {pips.map((p, idx) => (
        <ElementIcon key={`${p.element}-${idx}`} element={p.element} size={14} />
      ))}
    </div>
  );
}
